import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import { inject, Injectable } from '@angular/core';
import { Import } from '../type/import.type';
import { ImportService } from '../services/import.service';
import {
  AddFile,
  ImportBreadcrumbAction,
  InitImport, NewExtract,
  NewImport,
  SelectedImports,
  SendImports,
  UpdateLibelle,
  VisualizeEmissions
} from './import.actions';
import { ALLOWED_FILE_TYPES, FILE_SIZE_LIMIT, LIBELLE_MAX_LENGTH } from '../import.constants';
import { catchError, tap } from 'rxjs';
import { BreadcrumbItem } from 'src/app/shared/types/breadcrumb.type';
import { ImportStatus } from '../type/import_status.enum';
import { AnalyseState } from '../../analyse/state/analyse.state';

export type ImportStateModel = {
  imports: Import[];
  selectedImports: Import[];
  step: number;
  isLoading: boolean;
};

@State<ImportStateModel>({
  name: 'import',
  defaults: {
    imports: [],
    selectedImports: [],
    step: 1,
    isLoading: true
  }
})
@Injectable()
export class ImportState {
  private readonly fileService = inject(ImportService);
  private readonly store = inject(Store);

  protected static _isValidImport = (importItem: Import): boolean => {
    /// Si l'import a un status, c'est qu'il a déjà étemailType: string, selectedTitles: stringOn ne peut donc pas le modifier il est donc valide
    if (importItem.status !== undefined) {
      return true;
    }

    return (
      !!importItem.file &&
      ALLOWED_FILE_TYPES.includes(importItem.file.type) &&
      importItem.file.size <= FILE_SIZE_LIMIT &&
      !!importItem.label &&
      importItem.label.length <= LIBELLE_MAX_LENGTH
    );
  };

  @Selector()
  static getImports(state: ImportStateModel): Import[] {
    return state.imports;
  }

  @Selector()
  static getImportsWithStatus(state: ImportStateModel): Import[] {
    return state.imports.filter((importItem) => importItem.status);
  }

  @Selector()
  static hasNewFile(state: ImportStateModel): boolean {
    return (
      state.imports.every((importItem) => !importItem.status) ||
      state.imports.some((importItem) => !importItem.status && !!importItem.file && !!importItem.label)
    );
  }

  @Selector()
  static isLoading(state: ImportStateModel): boolean {
    return state.isLoading;
  }

  @Selector()
  static canImportOtherFile(state: ImportStateModel): boolean {
    return state.imports.every((importItem) => this._isValidImport(importItem));
  }

  @Selector()
  static canUpload(state: ImportStateModel): boolean {
    /// Si un seul import il doit être valide pour pouvoir passer à l'étape suivante
    if (state.imports.length === 1) {
      return this._isValidImport(state.imports[0]);
    }

    /// Dans les autres cas, tous les imports doivent être valides ou vide pour pouvoir passer à l'étape suivante
    return state.imports.every((importItem) => (!importItem.file && !importItem.label) || this._isValidImport(importItem));
  }

  @Selector()
  static canVisualize(state: ImportStateModel): boolean {
    return state.selectedImports.length > 0;
  }

  @Selector()
  static showAlertTreatmentInProgress(state: ImportStateModel): boolean {
    return state.imports.every((importItem) => importItem.status !== ImportStatus.TREATED);
  }

  @Selector()
  static getBreadcrumbs(state: ImportStateModel): BreadcrumbItem[] {
    const step = state.step;
    return [
      {
        step: 1,
        label: 'breadcrumb.measure.importData',
        isActive: step >= 1,
        isValidate: step > 1,
        action: step > 1 ? new ImportBreadcrumbAction(1) : undefined
      },
      {
        step: 2,
        label: 'breadcrumb.measure.treatData',
        isActive: step >= 2,
        isValidate: step > 2,
        action: step > 2 ? new ImportBreadcrumbAction(2) : undefined
      },
      {
        step: 3,
        label: 'breadcrumb.measure.viewData',
        isActive: step >= 3,
        isValidate: step > 3
      }
    ];
  }

  @Selector()
  static getStep(state: ImportStateModel): number {
    return state.step;
  }

  @Action(InitImport)
  initImport({ patchState }: StateContext<ImportStateModel>) {
    return this.fileService.getFiles().pipe(
      tap((imports) => {
        const hasParameters = this.store.selectSnapshot(AnalyseState.getParameters);
        const initialImports = imports.length ? imports : [{ id: this.fileService.generateUuid() }];
        const savedStep = localStorage.getItem('measureStep');
        const step = savedStep ? parseInt(savedStep, 10) : 1;

        patchState({
          imports: initialImports,
          step: step,
          isLoading: false
        });
      }),
      catchError(() => {
        patchState({
          imports: [{ id: this.fileService.generateUuid() }],
          step: 1,
          isLoading: false
        });

        throw new Error('error.getFiles');
      })
    );
  }

  @Action(NewImport)
  newImport({ getState, patchState }: StateContext<ImportStateModel>): void {
    const state = getState();
    patchState({
      imports: [...state.imports, { id: this.fileService.generateUuid() }]
    });
  }

  @Action(AddFile)
  addFile({ getState, patchState }: StateContext<ImportStateModel>, { index, file }: AddFile): void {
    const state = getState();
    const imports = state.imports.map((importItem, i) => (i === index ? { ...importItem, file } : importItem));
    patchState({
      imports
    });
  }

  @Action(UpdateLibelle)
  updateLibelle({ getState, patchState }: StateContext<ImportStateModel>, { index, libelle }: UpdateLibelle): void {
    const state = getState();
    const imports = state.imports.map((importItem, i) => (i === index ? { ...importItem, label: libelle } : importItem));

    patchState({
      imports
    });
  }

  @Action(ImportBreadcrumbAction)
  goToStep({ patchState }: StateContext<ImportStateModel>, { step }: ImportBreadcrumbAction): void {
    patchState({ step });
    localStorage.setItem('measureStep', step.toString());
  }

  @Action(SendImports)
  sendImports({ getState, patchState }: StateContext<ImportStateModel>) {
    const state = getState();

    patchState({
      isLoading: true
    });

    // Remove empty imports
    state.imports = state.imports.filter((importItem) => importItem.file && importItem.label);

    if (ImportState.canUpload(state) === true && state.imports.filter((importItem) => !importItem.status).length === 0) {
      // User come back to step 1 but no file has been changed
      patchState({
        step: 2,
        isLoading: false
      });

      return;
    }

    return this.fileService.uploadFiles({ files: state.imports }).pipe(
      tap((uploadedFiles) => {
        patchState({
          step: 2,
          // merge uploadedFiles with current imports by id
          imports: state.imports.map((importItem) => uploadedFiles.find((uploadedFile) => uploadedFile.id === importItem.id) || importItem),
          isLoading: false
        });
      }),
      catchError((error) => {
        patchState({ isLoading: false });
        throw error;
      })
    );
  }

  @Action(VisualizeEmissions)
  visualizeEmissions({ patchState }: StateContext<ImportStateModel>) {
    patchState({
      step: 3,
      isLoading: false
    });
  }

  @Action(SelectedImports)
  selectedImport({ patchState }: StateContext<ImportStateModel>, { imports }: SelectedImports): void {
    localStorage.setItem('selectedField from  import state', JSON.stringify(imports));
    patchState({
      selectedImports: imports
    });
  }

  @Action(NewExtract)
  extractFret21(_: StateContext<ImportStateModel>, { payload }: NewExtract) {
    return this.fileService.extractFret21(payload.emailType, payload.selectedFiles).pipe(
      catchError((error) => {
        throw error;
      })
    );
  }


}
